import type { OutcomeItem } from "@/services/financeiro";

function billIdKey(billId: string): string {
  return String(billId ?? "").trim();
}

function longestText(...parts: string[]): string {
  let best = "";
  for (const p of parts) {
    const t = (p || "").trim();
    if (t.length > best.length) best = t;
  }
  return best;
}

/**
 * Texto da coluna Notas: melhor candidato entre API Sienge, agregado bulk, linha e descrição
 * (evita mapa vazio durante o fetch e chave number vs string no Map).
 */
export function noteTextForItem(
  item: OutcomeItem,
  apiNotesByBillId: Map<string, string>,
  bulkNotesByBillId: Map<string, string>,
): string {
  const id = billIdKey(item.billId);
  if (!id) return "";
  const fromApi = (apiNotesByBillId.get(id) ?? apiNotesByBillId.get(item.billId) ?? "").trim();
  const fromBulkAgg = (bulkNotesByBillId.get(id) ?? bulkNotesByBillId.get(item.billId) ?? "").trim();
  const fromRow = (item.notes || "").trim();
  const fromDesc = (item.description || "").trim();
  return longestText(fromApi, fromBulkAgg, fromRow, fromDesc);
}

/** Pré-preenche o mapa com o melhor texto já disponível no bulk (sem esperar rede). */
export function seedNotesMapFromItems(items: OutcomeItem[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const it of items) {
    const bid = billIdKey(it.billId);
    if (!bid) continue;
    const best = longestText(it.notes || "", it.description || "");
    if (!best) continue;
    const prev = (m.get(bid) || "").trim();
    if (!prev || best.length > prev.length) m.set(bid, best);
  }
  return m;
}

/**
 * Extrai o número/id do pedido de compra a partir das notas do título
 * (ex.: "Nota fiscal associada ao pedido de compra: 4305").
 */
export function extractPurchaseOrderRefFromNotes(notes: string): string | null {
  const text = (notes || "").replace(/\s+/g, " ").trim();
  if (!text) return null;

  const patterns = [
    /pedido\s+de\s+compra\s*[:\-#]?\s*n?[º°.]?\s*(\d{2,})/i,
    /associad[oa]\s+ao\s+pedido(?:\s+de\s+compra)?\s*[:\-#]?\s*(\d{2,})/i,
    /\bPC\s*[:\-#]?\s*(\d{2,})\b/i,
    /pedido\s*[:\-#]\s*(\d{2,})/i,
  ];

  for (const re of patterns) {
    const m = text.match(re);
    if (m?.[1]) return m[1];
  }
  return null;
}
